"use client";

import { Button, Card, Spinner } from "./ui";
import { useCurrency } from "./currency-provider";

export interface TokenEstimateView {
  label: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  estimatedUsd: number;
  upperUsd: number;
  basis: string;
}

export function TokenCostDialog({
  estimate,
  confirming,
  onCancel,
  onConfirm,
}: {
  estimate: TokenEstimateView;
  confirming: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { currency, formatUsdValue } = useCurrency();
  const money = (value: number) => formatUsdValue(value, value < 0.01);
  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center bg-black/70 px-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="token-estimate-title"
    >
      <Card className="w-full max-w-md border-[var(--accent)]/35 p-6 shadow-2xl">
        <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-[var(--accent-2)]">
          Token & cost estimate
        </div>
        <h2 id="token-estimate-title" className="mt-2 text-xl font-semibold">
          {estimate.label}
        </h2>
        <div className="mt-5 grid grid-cols-3 gap-2 text-center">
          <Metric label="Input" value={estimate.inputTokens.toLocaleString()} />
          <Metric label="Output" value={estimate.outputTokens.toLocaleString()} />
          <Metric label="Total" value={estimate.totalTokens.toLocaleString()} />
        </div>
        <div className="mt-4 rounded-xl border border-[var(--accent)]/25 bg-[var(--accent)]/10 p-4">
          <div className="text-xs text-muted">Estimated analysis price</div>
          <div className="mt-1 text-2xl font-semibold text-[var(--accent-2)]">
            {money(estimate.estimatedUsd)}
          </div>
          <div className="mt-1 text-[11px] text-muted">
            Expected maximum {money(estimate.upperUsd)} · {currency}
            {currency !== "USD" ? " estimated" : ""}
          </div>
        </div>
        <dl className="mt-4 space-y-2 text-[12px] text-muted">
          <div className="flex gap-3"><dt className="w-14 shrink-0">Basis</dt><dd>{estimate.basis}</dd></div>
        </dl>
        <p className="mt-4 text-[11px] leading-5 text-muted">
          This is an estimate. Final usage can vary with page content and AI output. No analysis is started until you confirm.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" disabled={confirming} onClick={onCancel}>Cancel</Button>
          <Button disabled={confirming} onClick={onConfirm}>
            {confirming ? <><Spinner /> Starting…</> : "Confirm & analyze"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return <div className="rounded-xl bg-white/[0.05] px-2 py-3"><div className="text-[10px] uppercase tracking-wider text-muted">{label}</div><div className="mt-1 text-sm font-semibold">{value}</div><div className="text-[10px] text-muted">tokens</div></div>;
}
